import { NoteText } from "@/components/notes";
import { localized } from "@msuite/ui/localization";
import {
	createColumnHelper,
	createMeta,
	sizeColumns,
	createSubRowHandle,
	weightColumns,
} from "@msuite/ui/table";
import { Tag, YesNoTag } from "@msuite/ui/tag";
import { formatNumber } from "@msuite/utils/numbers";
import {
	getArticleNumberString,
	getArticleQuantityState,
	getArticleQuantityStateColor,
	getArticleQuantityStateTranslationKey,
} from "@msuite/utils/stock";
import type { ArticleListItem } from "@msuite/utils/types";

const columnHelper = createColumnHelper<ArticleListItem>();

/**
 * What this article has standing on one shelf. Only `articlesTableLocationColumns` renders it,
 * because the server leaves it null for a list that names no location and a column of zeros there
 * would read as an empty warehouse.
 */
const locationQuantityColumn = columnHelper.accessor("locationQuantity", {
	id: "locationQuantity",
	header: localized("stock.quantity_on_location"),
	cell: (cell) => (
		<div className="w-full h-stack justify-end pr-2">
			<Tag
				direction="row-reverse"
				color="emerald"
				variant="plain"
			>
				{formatNumber(cell.getValue(), "integer", 0)}
			</Tag>
		</div>
	),
});

export const columns = [
	createSubRowHandle<ArticleListItem>(),
	columnHelper.accessor(
		(row) =>
			getArticleNumberString(row.articleGroupNumber, row.articleProductTypeNumber, row.number),
		{
			id: "number",
			header: localized("general.number"),
			cell: (cell) => cell.getValue(),
			meta: createMeta({
				isTabular: true,
			}),
		},
	),
	columnHelper.accessor("name", {
		id: "name",
		header: localized("general.name"),
		cell: (cell) => cell.getValue(),
	}),
	columnHelper.accessor("hasLocation", {
		id: "hasLocation",
		header: localized("stock.has_location"),
		cell: (cell) => <YesNoTag value={cell.getValue()} />,
	}),
	columnHelper.accessor("articleGroupName", {
		id: "articleGroupName",
		header: localized("stock.article_group"),
		cell: (cell) => cell.getValue(),
	}),
	columnHelper.accessor("articleProductTypeName", {
		id: "articleProductTypeName",
		header: localized("stock.article_product_type"),
		cell: (cell) => cell.getValue(),
	}),
	columnHelper.accessor("description", {
		id: "description",
		header: localized("general.description"),
		cell: (cell) => <NoteText note={cell.getValue()} />,
		size: 350,
	}),
	columnHelper.accessor("stockBalance", {
		id: "stockBalance",
		header: localized("stock.stock_balance"),
		cell: (cell) => (
			<div className="w-full h-stack justify-end pr-2">
				<Tag
					direction="row-reverse"
					color="slate"
					variant="plain"
				>
					{formatNumber(cell.getValue(), "integer", 0)}
				</Tag>
			</div>
		),
	}),
	// The state grades what is left to promise, not what is on the shelf, so it is this column
	// that carries the tag: an article can stand full and still be unorderable down to the last
	// piece, and the two numbers have to be readable side by side for that to make sense.
	columnHelper.accessor("availableQuantity", {
		id: "availableQuantity",
		header: localized("stock.orderable_quantity"),
		cell: (cell) => {
			const state = getArticleQuantityState(cell.row.original);
			return (
				<div className="w-full h-stack justify-between pr-2">
					<p>{localized(getArticleQuantityStateTranslationKey(state))}</p>
					<Tag
						direction="row-reverse"
						color={getArticleQuantityStateColor(state)}
						variant="plain"
					>
						{formatNumber(cell.getValue(), "integer", 0)}
					</Tag>
				</div>
			);
		},
	}),
	columnHelper.accessor("reservedQuantity", {
		id: "reservedQuantity",
		header: localized("stock.reserved_quantity"),
		cell: (cell) => (
			<div className="w-full h-stack justify-end pr-2">
				<Tag
					direction="row-reverse"
					color="blue"
					variant="plain"
				>
					{formatNumber(cell.getValue(), "integer", 0)}
				</Tag>
			</div>
		),
	}),
	columnHelper.accessor("warningStockBalance", {
		id: "warningStockBalance",
		header: localized("stock.warning_stock_balance"),
		cell: (cell) => (
			<Tag
				direction="row-reverse"
				color="amber"
				variant="plain"
			>
				{formatNumber(cell.getValue(), "integer")}
			</Tag>
		),
		meta: createMeta({
			isNumeric: true,
		}),
	}),
	columnHelper.accessor("minimumStockBalance", {
		id: "minimumStockBalance",
		header: localized("stock.minimum_stock_balance"),
		cell: (cell) => (
			<Tag
				direction="row-reverse"
				color="red"
				variant="plain"
			>
				{formatNumber(cell.getValue(), "integer")}
			</Tag>
		),
		meta: createMeta({
			isNumeric: true,
		}),
	}),
	...sizeColumns<ArticleListItem>(),
	...weightColumns<ArticleListItem>(),
];

/**
 * The same set for a list narrowed to one shelf, with that shelf's figure sitting beside the
 * warehouse-wide balance. Both numbers matter together: 40 on this shelf out of 400 in the
 * building is a different answer to "is it here" than 40 out of 40.
 */
export const columnsWithLocationQuantity = columns.flatMap((column) =>
	column.id === "stockBalance" ? [column, locationQuantityColumn] : [column],
);
